import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { BHRIGU_FACE } from 'features/ai/lib/askGuideIdentity';

const HEARTBEAT_INTERVAL_MS = 10_000;
const HEARTBEAT_DURATION_MS = 2_400;

/**
 * Floating Ask guide face button with periodic heartbeat nudge.
 * Transparent, oversized portrait - no filled background disc.
 * @param {{
 *   isDark: boolean;
 *   open: boolean;
 *   onClick: () => void;
 *   pageKey: string;
 *   ariaLabel: string;
 *   faceUrl?: string;
 *   faceObjectPosition?: 'center' | 'top';
 *   title?: string;
 * }} props
 */
export const BhriguChatTrigger = forwardRef(function BhriguChatTrigger(
  {
    isDark,
    open,
    onClick,
    pageKey,
    ariaLabel,
    faceUrl = BHRIGU_FACE,
    faceObjectPosition = 'center',
    title = 'AskGuru',
  },
  ref,
) {
  const [heartbeatActive, setHeartbeatActive] = useState(false);
  const intervalRef = useRef(null);
  const heartbeatTimeoutRef = useRef(null);
  const reducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const clearHeartbeatTimeout = () => {
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    clearHeartbeatTimeout();
    setHeartbeatActive(false);

    if (reducedMotion || open) return undefined;

    const triggerHeartbeat = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;

      clearHeartbeatTimeout();
      setHeartbeatActive(true);
      heartbeatTimeoutRef.current = setTimeout(() => {
        setHeartbeatActive(false);
        heartbeatTimeoutRef.current = null;
      }, HEARTBEAT_DURATION_MS);
    };

    intervalRef.current = setInterval(triggerHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      clearHeartbeatTimeout();
      setHeartbeatActive(false);
    };
  }, [pageKey, reducedMotion, open]);

  const ringClass = open
    ? isDark
      ? 'ring-2 ring-amber-300/50'
      : 'ring-2 ring-amber-500/40'
    : '';

  return (
    <div className="pointer-events-auto h-[4.25rem] w-[4.25rem] shrink-0 sm:h-[4.75rem] sm:w-[4.75rem]">
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        className={`page-feedback-trigger page-bhrigu-trigger relative isolate flex h-full w-full min-h-[44px] min-w-[44px] shrink-0 items-center justify-center overflow-visible rounded-full border-0 bg-transparent p-0 shadow-none transition-transform hover:scale-105 ${
          heartbeatActive ? 'page-bhrigu-trigger--heartbeat' : ''
        } ${ringClass}`}
        aria-expanded={open}
        aria-label={ariaLabel}
        title={title}
      >
        {heartbeatActive ? (
          <>
            <span className="page-bhrigu-trigger-ring" aria-hidden />
            <span className="page-bhrigu-trigger-ring page-bhrigu-trigger-ring--second" aria-hidden />
          </>
        ) : null}
        <span
          className="pointer-events-none absolute inset-[10%] overflow-hidden rounded-full bg-transparent"
          aria-hidden
        >
          <img
            src={faceUrl}
            alt=""
            className={`h-full w-full bg-transparent object-cover drop-shadow-md ${
              faceObjectPosition === 'top' ? 'object-top' : 'object-center'
            }`}
            decoding="async"
          />
        </span>
      </button>
    </div>
  );
});

export default BhriguChatTrigger;
